import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties, Ref } from 'vue'

import { DisciplineFilesModal } from './enums'
import { useDisciplineFilesStore } from './files/store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'

interface DisciplineFilesSendMenuReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  canManageFiles: ComputedRef<boolean>
  canManageDisciplines: ComputedRef<boolean>
  toggle: () => void
  openSendFiles: () => void
  openCertificate: () => void
}

/** The legacy "Send file" button: a menu with "Generate certificate" and "Send files". */
export const useDisciplineFilesSendMenu = (): DisciplineFilesSendMenuReturn => {
  const store = useDisciplineFilesStore()
  const { canManageFiles, canManageDisciplines } = useDisciplinePrivileges()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const open = (modal: DisciplineFilesModal): void => {
    isOpen.value = false
    store.openModal(modal)
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    canManageFiles,
    canManageDisciplines,
    toggle,
    openSendFiles: () => open(DisciplineFilesModal.Send),
    openCertificate: () => open(DisciplineFilesModal.Certificate),
  }
}
